// React
import { useState, useEffect, useCallback } from 'react'

// Components
import { BaseDialog } from 'components'
import { Table } from './components'
import { UserDetailDialog } from 'pages/back-office/user/components'

// Interfaces
import { IUserBulkRegisterDialogProps } from './interfaces'
import { IUserDetail, IUserUpdateBulkInsertForm, IUserUpdateForm } from 'interfaces/back-office/user'

// Utils
import { commonUtils_delay } from 'utils/common/common.utils'
import { messageUtils_error } from 'utils/common/message.utils'

// Lodash
import omit from 'lodash/omit'

// i18n
import { useTranslation } from 'react-i18next'

// Mobx React
import { observer } from 'mobx-react'

const UserBulkRegisterDialog = observer(
  ({ isOpen, loading, onClickFirstButton, onClickCloseButton }: IUserBulkRegisterDialogProps) => {
    // Hook
    const [isDynamicFormLoading, setDynamicFormLoading] = useState<boolean>(false)
    const [dynamicForms, setDynamicForms] = useState<IUserUpdateBulkInsertForm[]>([])
    const [currentDynamicFormId, setCurrentDynamicFormId] = useState<string>('')
    const [dialog, setDialog] = useState<{ isCreateUserDialogOpen: boolean }>({
      isCreateUserDialogOpen: false,
    })
    const { t } = useTranslation()

    // Do when user inside the modal
    useEffect(() => {
      if (isOpen) onClear()

      // eslint-disable-next-line
    }, [isOpen])

    /**
     * @description Clear state from current component
     *
     * @return {void}
     */
    const onClear = useCallback((): void => {
      setDynamicForms([])
    }, [])

    /**
     * @description Handle dialog
     *
     * @param {string} type
     * @param {boolean} value
     *
     * @return {void}
     */
    const handleDialog = useCallback((type: 'isCreateUserDialogOpen', value: boolean): void => {
      setDialog(previousDialog => ({ ...previousDialog, [type]: value }))

      if (!value) {
        setCurrentDynamicFormId('')
      }
    }, [])

    /**
     * @description Handle dynamic form loading
     *
     * @return {Promise<void>} Promise<void>
     */
    const handleDynamicFormLoading = useCallback(async (): Promise<void> => {
      setDynamicFormLoading(true)

      await commonUtils_delay(300)

      setDynamicFormLoading(false)
    }, [])

    /**
     * @description Watch any change when user clicking add user button
     *
     * @return {void} void
     */
    const onAddDynamicUser = useCallback(() => {
      handleDialog('isCreateUserDialogOpen', true)
    }, [handleDialog])

    /**
     * @description Handle edit dynamic user
     *
     * @param {string} id
     * @param {boolean} value
     *
     * @return {void} void
     */
    const onEditDynamicUser = useCallback(
      (id: string): void => {
        handleDialog('isCreateUserDialogOpen', true)

        setCurrentDynamicFormId(id)
      },
      [handleDialog],
    )

    /**
     * @description Save dynamic forms
     *
     * @param {IUserUpdateForm} form
     *
     * @return {Promise<void>} Promise<void>
     */
    const onSaveDynamicForm = useCallback(
      async (form?: IUserUpdateForm): Promise<void> => {
        const mapForm = form as IUserUpdateBulkInsertForm

        // Wait for loading
        await handleDynamicFormLoading()

        // Close the modal
        handleDialog('isCreateUserDialogOpen', false)

        // Append the form
        if (currentDynamicFormId)
          setDynamicForms(previousDynamicForms =>
            previousDynamicForms.map(dynamicForm => (dynamicForm.id === currentDynamicFormId ? mapForm : dynamicForm)),
          )
        else setDynamicForms(previousDynamicForms => [...previousDynamicForms, mapForm])
      },
      [currentDynamicFormId, handleDialog, handleDynamicFormLoading],
    )

    /**
     * @description Remove dynamic form
     *
     * @param {string} id
     *
     * @return {void} void
     */
    const onRemoveDynamicForm = useCallback((id: string): void => {
      setDynamicForms(previousDynamicForms => previousDynamicForms.filter(form => form.id !== id))
    }, [])

    /**
     * @description Check before emitting the onClickFirstButton method
     *
     * @return {void} void
     */
    const onSubmit = useCallback((): void => {
      // Check for dynamic forms length
      if (dynamicForms.length === 0) {
        messageUtils_error(t('user.bulkRegister.validations.formLengthInvalid'))

        return
      }

      const mapDynamicForms = dynamicForms.map(dynamicForm => {
        return omit(dynamicForm, ['id', 'companyName', 'fieldName'])
      }) as IUserUpdateForm[]

      onClickFirstButton(mapDynamicForms)
    }, [dynamicForms, t, onClickFirstButton])

    /**
     * @description Render body
     *
     * @return {JSX.Element} JSX.Element
     */
    const renderBody = useCallback((): JSX.Element => {
      return (
        <Table
          loading={loading.isActionLoading}
          forms={dynamicForms}
          add={() => onAddDynamicUser()}
          remove={onRemoveDynamicForm}
          edit={onEditDynamicUser}
        />
      )
    }, [loading.isActionLoading, dynamicForms, onAddDynamicUser, onEditDynamicUser, onRemoveDynamicForm])

    return (
      <>
        {/* Base Dialog For Bulk Register */}
        <BaseDialog
          title={`${`user.bulkRegister.bulkRegister`}`}
          isOpen={isOpen}
          closable={!loading.isActionLoading}
          body={renderBody()}
          firstButtonLabel="app.save"
          onFirstButtonClick={onSubmit}
          disableFirstButton={dynamicForms.length === 0}
          onButtonCloseClick={() => onClickCloseButton(false)}
          isFirstButtonLoading={loading.isActionLoading || loading.isDetailLoading}
          isUsingDefaultFooter
          forceRender
        />

        {/*  Form For Add User */}
        <UserDetailDialog
          isOpen={dialog.isCreateUserDialogOpen}
          loading={loading}
          attrs={{
            user: {} as IUserDetail,
            dynamicForms,
            userDynamic: dynamicForms?.find(dynamicForm => dynamicForm.id === currentDynamicFormId),
            isUserActionLoading: isDynamicFormLoading,
            closeModalCallback: () => handleDialog('isCreateUserDialogOpen', false),
          }}
          onClickFirstButton={onSaveDynamicForm}
          onClickCloseButton={() => handleDialog('isCreateUserDialogOpen', false)}
          isCreate
          isEditable
          noNik
        />
      </>
    )
  },
)

export { UserBulkRegisterDialog }
